import { AlertTriangle, Camera, Loader2, RefreshCcw, ScanFace, Slash, Square, X } from "lucide-react";
import { useCallback, useEffect, useRef, useState } from "react";
import { Button } from "./Button";
import Teleprompter from "./Teleprompter";
import CaptionGuideOverlay from "./CaptionGuideOverlay";
import { useFaceAlignment } from "../../hooks/useFaceAlignment";
import { noseTarget } from "../../utils/captionGuide";
import { cn } from "../../utils/classMerger";
import type { CaptionPosition } from "../../types/captionPosition";
import type { CaptionStyle } from "../../types/captionStyle";

export interface VideoRecorderModalProps {
    onRecordingComplete: (file: File) => void;
    onClose: () => void;
    /**
     * When true, the recorded video is canvas-cropped to a fixed 16:9 (landscape)
     * or 9:16 (portrait) aspect ratio at 1920×1080 / 1080×1920. Used by the
     * ZapCap Caption Video flow on desktop, where webcams deliver non-standard
     * sensor ratios (e.g. 1920×1440) that break caption placement.
     */
    enforceAspectRatio?: boolean;
    /**
     * When true, force portrait orientation and hide the landscape toggle.
     * Used by the ZapCap Caption Video flow — captioning is built for 9:16
     * social formats and doesn't render well on landscape webcam recordings.
     */
    portraitOnly?: boolean;
    /**
     * My Niche Video: a script shown over the top of the preview
     * while recording. On-screen only — the recorder captures camera frames,
     * so it never appears in the video. Absent: the modal is unchanged.
     */
    teleprompterScript?: string;
    /**
     * The captioned flows: draw where the captions will land and where to stand,
     * with a Guides button to hide them. Absent: the modal is unchanged.
     */
    captionGuide?: { position: CaptionPosition; style: CaptionStyle; enabled: boolean; onToggle: () => void };
}

const MAX_RECORDING_SECONDS = 120; // 2 minutes
const TARGET_LANDSCAPE_W = 1920;
const TARGET_LANDSCAPE_H = 1080;
const TARGET_PORTRAIT_W = 1080;
const TARGET_PORTRAIT_H = 1920;

type FacingMode = "environment" | "user";
type Orientation = "landscape" | "portrait";

function formatTime(seconds: number): string {
    const m = Math.floor(seconds / 60);
    const s = seconds % 60;
    return `${m}:${s.toString().padStart(2, "0")}`;
}

function buildVideoConstraints(
    orientation: Orientation,
    facingMode: FacingMode
): MediaTrackConstraints {
    if (orientation === "landscape") {
        return {
            width: { min: 960, ideal: 1920 },
            height: { min: 540, ideal: 1080 },
            facingMode,
        };
    }
    return {
        width: { min: 540, ideal: 1080 },
        height: { min: 960, ideal: 1920 },
        facingMode,
    };
}

const log = (...args: unknown[]) =>
    console.log("[VideoRecorder]", ...args);

const VideoRecorderModal: React.FC<VideoRecorderModalProps> = ({
    onRecordingComplete,
    onClose,
    enforceAspectRatio = false,
    portraitOnly = false,
    teleprompterScript,
    captionGuide,
}) => {
    const videoRef = useRef<HTMLVideoElement>(null);
    const streamRef = useRef<MediaStream | null>(null);
    const mediaRecorderRef = useRef<MediaRecorder | null>(null);
    const chunksRef = useRef<BlobPart[]>([]);
    const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);
    const discardRef = useRef(false);
    const animFrameRef = useRef<number | null>(null);
    const canvasStreamRef = useRef<MediaStream | null>(null);

    // Hide the sidebar while the recorder is open
    useEffect(() => {
        const sidebar = document.getElementById("app-sidebar");
        if (sidebar) sidebar.style.visibility = "hidden";
        return () => {
            if (sidebar) sidebar.style.visibility = "";
        };
    }, []);

    const [isRecording, setIsRecording] = useState(false);
    const [elapsed, setElapsed] = useState(0);
    const [isCameraReady, setIsCameraReady] = useState(false);
    // isRestarting = true when switching orientation or flipping camera.
    // Used to suppress the "Starting…" spinner in the Start button (the stream
    // is already proven to work; we're just reconfiguring it).
    const [isRestarting, setIsRestarting] = useState(false);
    const [facingMode, setFacingMode] = useState<FacingMode>("user");
    const [orientation, setOrientation] = useState<Orientation>(
        portraitOnly ? "portrait" : "landscape"
    );
    const [permissionError, setPermissionError] = useState<string | null>(null);
    const [resolutionWarning, setResolutionWarning] = useState<string | null>(null);
    const [postRecordError, setPostRecordError] = useState<string | null>(null);
    // Shown when the device can't deliver the requested orientation at HD
    // (e.g. desktop webcam asked for portrait). Auto-reverts to the previous
    // orientation/facing so the camera doesn't go dark.
    const [unsupportedError, setUnsupportedError] = useState<string | null>(null);

    const mimeType = useRef(
        MediaRecorder.isTypeSupported("video/mp4")
            ? "video/mp4"
            : "video/webm"
    ).current;

    const stopStream = useCallback(() => {
        log("stopStream() called");
        log("  streamRef.current:", streamRef.current);
        log("  videoRef.current:", videoRef.current);

        if (animFrameRef.current !== null) {
            cancelAnimationFrame(animFrameRef.current);
            animFrameRef.current = null;
        }
        canvasStreamRef.current?.getTracks().forEach((t) => t.stop());
        canvasStreamRef.current = null;

        const tracks = streamRef.current?.getTracks() ?? [];
        log("  tracks before stop:", tracks.map((t) => `${t.kind}:${t.readyState}`));
        tracks.forEach((t) => t.stop());
        log("  tracks after stop:", tracks.map((t) => `${t.kind}:${t.readyState}`));

        streamRef.current = null;
        mediaRecorderRef.current = null;

        if (videoRef.current) {
            videoRef.current.srcObject = null;
            log("  video.srcObject cleared");
        } else {
            log("  videoRef.current is NULL — srcObject NOT cleared");
        }
    }, []);

    // Per-invocation signal fixes React StrictMode double-mount:
    // two async getUserMedia calls race, and the first (cancelled) one
    // stops its own stream when it resolves rather than leaking it.
    // Returns "ok" | "overconstrained" | "failed" so the caller can decide
    // whether to revert orientation/facing after an unsupported request.
    const startStream = useCallback(
        async (
            newOrientation: Orientation,
            newFacingMode: FacingMode,
            signal: { cancelled: boolean }
        ): Promise<"ok" | "overconstrained" | "failed" | "cancelled"> => {
            // When enforceAspectRatio is on, the canvas-recording layer crops
            // to the target orientation — so always ask the camera for landscape
            // (every webcam can deliver it) and let the crop produce portrait.
            const streamOrientation: Orientation = enforceAspectRatio
                ? "landscape"
                : newOrientation;
            log("startStream() →", newOrientation, newFacingMode, "stream:", streamOrientation);
            setIsCameraReady(false);
            setResolutionWarning(null);
            setPermissionError(null);

            try {
                const stream = await navigator.mediaDevices.getUserMedia({
                    video: buildVideoConstraints(streamOrientation, newFacingMode),
                    audio: true,
                });

                if (signal.cancelled) {
                    log("startStream() — cancelled, stopping leaked stream");
                    stream.getTracks().forEach((t) => t.stop());
                    return "cancelled";
                }

                streamRef.current = stream;
                if (videoRef.current) {
                    videoRef.current.srcObject = stream;
                }

                const track = stream.getVideoTracks()[0];
                const { width = 0, height = 0 } = track.getSettings();
                log("  camera negotiated:", width, "×", height);

                const minW = streamOrientation === "landscape" ? 960 : 540;
                const minH = streamOrientation === "landscape" ? 540 : 960;
                if (width < minW || height < minH) {
                    setResolutionWarning(
                        `Camera is running at ${width}×${height}. Quality may be below minimum (${minW}×${minH}).`
                    );
                }

                setIsCameraReady(true);
                log("startStream() — camera ready");
                return "ok";
            } catch (err) {
                if (signal.cancelled) return "cancelled";
                log("startStream() — error:", err);
                // OverconstrainedError = the device cannot deliver a stream that
                // meets our Instagram-minimum constraints (typically a desktop
                // webcam asked to produce portrait, where the sensor is fixed
                // landscape). Surface a specific message and let the caller revert.
                if (err instanceof DOMException && err.name === "OverconstrainedError") {
                    return "overconstrained";
                }
                setPermissionError(
                    err instanceof DOMException && err.name === "NotAllowedError"
                        ? "Camera access was denied. Please allow camera and microphone permissions in your browser settings and try again."
                        : "Could not access the camera. Make sure no other app is using it and try again."
                );
                return "failed";
            }
        },
        [enforceAspectRatio]
    );

    // Cancel / X — stop stream immediately, discard any recording in progress
    const handleClose = useCallback(() => {
        log("handleClose() called");
        if (timerRef.current) {
            clearInterval(timerRef.current);
            timerRef.current = null;
        }
        const recorder = mediaRecorderRef.current;
        log("  recorder state:", recorder?.state ?? "null");
        if (recorder && recorder.state !== "inactive") {
            log("  recorder active → discard path");
            discardRef.current = true;
            stopStream();
            try { recorder.stop(); } catch (e) { log("  recorder.stop() threw:", e); }
            onClose();
        } else {
            log("  no active recorder → direct close");
            stopStream();
            onClose();
        }
    }, [stopStream, onClose]);

    // Stop button — recorder flushes cleanly, onstop releases stream
    const stopRecording = useCallback(() => {
        log("stopRecording() called");
        if (timerRef.current) {
            clearInterval(timerRef.current);
            timerRef.current = null;
        }
        const recorder = mediaRecorderRef.current;
        log("  recorder state:", recorder?.state ?? "null");
        if (recorder && recorder.state !== "inactive") {
            recorder.stop();
            log("  recorder.stop() called — waiting for onstop");
        }
        setIsRecording(false);
    }, []);

    useEffect(() => {
        const signal = { cancelled: false };
        log("useEffect mount — starting camera");
        void (async () => {
            const result = await startStream(orientation, facingMode, signal);
            if (result === "overconstrained" && !signal.cancelled) {
                setUnsupportedError(
                    `Your device doesn't support ${orientation} video at the minimum HD resolution.`
                );
            }
        })();

        return () => {
            log("useEffect cleanup — component unmounting");
            signal.cancelled = true;
            stopStream();
            if (timerRef.current) clearInterval(timerRef.current);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        if (isRecording && elapsed >= MAX_RECORDING_SECONDS) {
            log("Auto-stop at max duration");
            stopRecording();
        }
    }, [elapsed, isRecording, stopRecording]);

    const handleFlipCamera = async () => {
        if (isRecording || isRestarting) return;
        const next: FacingMode = facingMode === "environment" ? "user" : "environment";
        setIsRestarting(true);
        setUnsupportedError(null);
        stopStream();
        const result = await startStream(orientation, next, { cancelled: false });
        if (result === "ok") {
            setFacingMode(next);
        } else if (result === "overconstrained") {
            setUnsupportedError(
                `This camera can't deliver ${orientation} video at the minimum HD resolution. Reverting to the previous camera.`
            );
            await startStream(orientation, facingMode, { cancelled: false });
        }
        setIsRestarting(false);
    };

    const handleToggleOrientation = async () => {
        if (isRecording || isRestarting) return;
        const next: Orientation = orientation === "landscape" ? "portrait" : "landscape";
        // With enforceAspectRatio, the underlying stream is always landscape and
        // portrait is produced by canvas crop — no renegotiation, no failure path.
        if (enforceAspectRatio) {
            setUnsupportedError(null);
            setOrientation(next);
            return;
        }
        setIsRestarting(true);
        setUnsupportedError(null);
        stopStream();
        const result = await startStream(next, facingMode, { cancelled: false });
        if (result === "ok") {
            setOrientation(next);
        } else if (result === "overconstrained") {
            setUnsupportedError(
                `Your device doesn't support ${next} video at the minimum HD resolution (${next === "portrait" ? "540×960" : "960×540"}). Staying in ${orientation} mode.`
            );
            await startStream(orientation, facingMode, { cancelled: false });
        }
        setIsRestarting(false);
    };

    const startRecording = () => {
        if (!streamRef.current || !isCameraReady) return;
        log("startRecording()");

        chunksRef.current = [];
        discardRef.current = false;
        setElapsed(0);
        setPostRecordError(null);

        const currentOrientation = orientation;

        let recordStream: MediaStream = streamRef.current;
        const isMirrored = facingMode === "user";
        const useCanvasPath =
            (isMirrored || enforceAspectRatio) && videoRef.current;
        if (useCanvasPath) {
            const video = videoRef.current!;
            const canvas = document.createElement("canvas");
            if (enforceAspectRatio) {
                // Lock output to a standard 16:9 / 9:16 frame — desktop webcams
                // deliver non-standard sensor ratios (e.g. 1920×1440) that break
                // ZapCap caption placement.
                canvas.width =
                    currentOrientation === "landscape"
                        ? TARGET_LANDSCAPE_W
                        : TARGET_PORTRAIT_W;
                canvas.height =
                    currentOrientation === "landscape"
                        ? TARGET_LANDSCAPE_H
                        : TARGET_PORTRAIT_H;
            } else {
                canvas.width = video.videoWidth || 1920;
                canvas.height = video.videoHeight || 1080;
            }
            const ctx = canvas.getContext("2d")!;
            const drawFrame = () => {
                const sw = video.videoWidth;
                const sh = video.videoHeight;
                let sx = 0;
                let sy = 0;
                let sWidth = sw;
                let sHeight = sh;
                if (enforceAspectRatio && sw && sh) {
                    const targetAspect = canvas.width / canvas.height;
                    const sourceAspect = sw / sh;
                    if (sourceAspect > targetAspect) {
                        // Source wider than target — crop sides
                        sWidth = Math.round(sh * targetAspect);
                        sx = Math.round((sw - sWidth) / 2);
                    } else if (sourceAspect < targetAspect) {
                        // Source taller than target — crop top/bottom
                        sHeight = Math.round(sw / targetAspect);
                        sy = Math.round((sh - sHeight) / 2);
                    }
                }
                ctx.save();
                if (isMirrored) {
                    ctx.translate(canvas.width, 0);
                    ctx.scale(-1, 1);
                }
                if (sw && sh) {
                    ctx.drawImage(
                        video,
                        sx,
                        sy,
                        sWidth,
                        sHeight,
                        0,
                        0,
                        canvas.width,
                        canvas.height,
                    );
                }
                ctx.restore();
                animFrameRef.current = requestAnimationFrame(drawFrame);
            };
            drawFrame();
            const canvasStream = canvas.captureStream(30);
            streamRef.current.getAudioTracks().forEach((t) => canvasStream.addTrack(t));
            canvasStreamRef.current = canvasStream;
            recordStream = canvasStream;
        }

        const recorder = new MediaRecorder(recordStream, { mimeType });
        mediaRecorderRef.current = recorder;
        log("  MediaRecorder created, mimeType:", mimeType, "mirrored:", facingMode === "user");

        recorder.ondataavailable = (e) => {
            if (e.data.size > 0) chunksRef.current.push(e.data);
        };

        recorder.onstop = () => {
            log("recorder.onstop fired — discardRef:", discardRef.current);
            stopStream();

            if (discardRef.current) {
                log("  discard path — modal already closed");
                discardRef.current = false;
                return;
            }

            log("  normal path — validating blob");
            const blob = new Blob(chunksRef.current, { type: mimeType });
            log("  blob size:", blob.size);
            const url = URL.createObjectURL(blob);
            const vid = document.createElement("video");
            vid.src = url;

            vid.onloadedmetadata = () => {
                URL.revokeObjectURL(url);
                log("  video metadata:", vid.videoWidth, "×", vid.videoHeight);
                const minW = currentOrientation === "landscape" ? 960 : 540;
                const minH = currentOrientation === "landscape" ? 540 : 960;
                if (vid.videoWidth < minW || vid.videoHeight < minH) {
                    setPostRecordError(
                        `Recording resolution (${vid.videoWidth}×${vid.videoHeight}) is below the minimum ${minW}×${minH}. Please try recording again.`
                    );
                    setIsRecording(false);
                    return;
                }
                const ext = mimeType.includes("mp4") ? "mp4" : "webm";
                const file = new File([blob], `recorded-video-${Date.now()}.${ext}`, { type: mimeType });
                log("  handing off file:", file.name, file.size);
                onRecordingComplete(file);
                onClose();
            };

            vid.onerror = () => {
                URL.revokeObjectURL(url);
                log("  video metadata error");
                setPostRecordError("Could not verify the recorded video. Please try again.");
                setIsRecording(false);
            };
        };

        recorder.start(100);
        setIsRecording(true);
        log("  recording started");

        timerRef.current = setInterval(() => {
            setElapsed((prev) => prev + 1);
        }, 1000);
    };

    const progress = Math.min((elapsed / MAX_RECORDING_SECONDS) * 100, 100);

    // Only show spinner in the Start button during initial camera load.
    // When restarting (flip / orientation change), the stream is already proven
    // to work — show "Start Recording" as disabled instead.
    const showButtonSpinner = !isCameraReady && !isRestarting;
    const cameraLoading = !isCameraReady || isRestarting;
    const showVideoOverlay = cameraLoading && !isRecording;
    // The <video>'s box when enforceAspectRatio holds it to a ratio.
    const videoAspectRatio = orientation === "landscape" ? "16 / 9" : "9 / 16";
    const teleprompter = teleprompterScript?.trim() ? (
        <Teleprompter script={teleprompterScript} running={isRecording} />
    ) : null;
    // Portrait only: the captioned recorder never records landscape, and the guide has no landscape numbers.
    const guideShown = !!captionGuide?.enabled && orientation === "portrait";
    // Face tracking runs with the guide before recording, never while recording or with guides off, and only in
    // the 9:16 cover box (enforceAspectRatio) its maths assumes. Advisory: Record never waits on it, and it
    // falls back to the plain target if it cannot load.
    const alignment = useFaceAlignment({
        video: videoRef,
        enabled: guideShown && enforceAspectRatio && !isRecording,
        mirrored: facingMode === "user",
        target: captionGuide ? noseTarget(captionGuide.position, !!teleprompter, captionGuide.style) : { nose: 50, zoneBottom: 67 },
    });
    const guide = captionGuide && guideShown ? (
        <CaptionGuideOverlay
            position={captionGuide.position}
            captionStyle={captionGuide.style}
            prompterOnTop={!!teleprompter}
            recording={isRecording}
            alignment={alignment}
        />
    ) : null;

    // Single responsive layout — CSS sm: breakpoint handles mobile vs desktop.
    // Avoids JS window.innerWidth detection which is unreliable in DevTools simulation.
    return (
        <div className="fixed inset-0 z-[1000] bg-black sm:bg-black/80">
            <div className="flex flex-col h-full w-full overflow-hidden">

                {/* Header */}
                <div className="shrink-0 flex items-center justify-between px-4 sm:px-5 py-3 sm:py-4 bg-black/70 sm:bg-white border-b border-white/10 sm:border-gray-200">
                    <h2 className="flex items-center gap-2 font-semibold sm:font-bold text-base sm:text-lg text-white sm:text-primary">
                        <Camera className="h-5 w-5" />
                        Record Video
                    </h2>
                    <div className="flex items-center gap-3">
                        {isRecording && (
                            <span className="flex items-center gap-1.5 text-sm font-semibold text-red-400">
                                <span className="h-2 w-2 rounded-full bg-red-500" />
                                REC {formatTime(elapsed)}
                            </span>
                        )}
                        <button
                            className="text-white/70 hover:text-white sm:text-gray-400 sm:hover:text-red-500 transition-colors"
                            onClick={handleClose}
                            aria-label="Close"
                        >
                            <X className="h-5 w-5" />
                        </button>
                    </div>
                </div>

                {/* Banners */}
                {unsupportedError && !permissionError && (
                    <div className="shrink-0 flex items-start gap-2 bg-red-900/80 sm:bg-red-50 border-b border-red-700/30 sm:border-red-200 px-4 sm:px-5 py-2 text-sm text-red-200 sm:text-red-700">
                        <AlertTriangle className="h-4 w-4 mt-0.5 flex-shrink-0 text-red-400 sm:text-red-500" />
                        {unsupportedError}
                    </div>
                )}
                {resolutionWarning && !permissionError && (
                    <div className="shrink-0 flex items-start gap-2 bg-amber-900/80 sm:bg-amber-50 border-b border-amber-700/30 sm:border-amber-200 px-4 sm:px-5 py-2 text-sm text-amber-200 sm:text-amber-800">
                        <AlertTriangle className="h-4 w-4 mt-0.5 flex-shrink-0 text-amber-400 sm:text-amber-500" />
                        {resolutionWarning}
                    </div>
                )}
                {postRecordError && (
                    <div className="shrink-0 flex items-start gap-2 bg-red-900/80 sm:bg-red-50 border-b border-red-700/30 sm:border-red-200 px-4 sm:px-5 py-2 text-sm text-red-200 sm:text-red-700">
                        <AlertTriangle className="h-4 w-4 mt-0.5 flex-shrink-0 text-red-400 sm:text-red-500" />
                        {postRecordError}
                    </div>
                )}

                {/* Camera area — fills remaining height */}
                <div className="relative bg-black flex items-center justify-center overflow-hidden flex-1 min-h-0">
                    {permissionError ? (
                        <div className="flex flex-col items-center justify-center gap-3 p-8 text-center">
                            <Camera className="h-12 w-12 text-gray-500" />
                            <p className="text-sm text-gray-300 max-w-xs">{permissionError}</p>
                            <Button variant="outline" size="sm" onClick={handleClose}>Close</Button>
                        </div>
                    ) : (
                        <>
                            <video
                                ref={videoRef}
                                autoPlay
                                muted
                                playsInline
                                className={
                                    enforceAspectRatio
                                        ? "max-w-full max-h-full object-cover"
                                        : "w-full h-full object-contain"
                                }
                                style={{
                                    ...(facingMode === "user" ? { transform: "scaleX(-1)" } : {}),
                                    ...(enforceAspectRatio
                                        ? {
                                              aspectRatio: videoAspectRatio,
                                              height: "100%",
                                          }
                                        : {}),
                                }}
                            />
                            {/* Before the loading screen: same z-20, so the loading screen covers it. */}
                            {!enforceAspectRatio && guide}
                            {showVideoOverlay && (
                                // Above the teleprompter (z-10), which stays mounted so a flip keeps its speed and size.
                                <div data-testid="camera-loading" className="absolute inset-0 z-20 flex items-center justify-center bg-black">
                                    <Loader2 className="h-10 w-10 text-white animate-spin" />
                                </div>
                            )}
                            {(teleprompter || guide) &&
                                (enforceAspectRatio ? (
                                    // Matches the video's box, so on a wide desktop camera area the script
                                    // and the guides sit over the video, not over the black bars beside it.
                                    <div
                                        data-testid="teleprompter-frame"
                                        className="pointer-events-none absolute left-1/2 top-0 z-10 max-w-full -translate-x-1/2"
                                        style={{ aspectRatio: videoAspectRatio, height: "100%" }}
                                    >
                                        {teleprompter}
                                        {guide}
                                    </div>
                                ) : (
                                    teleprompter
                                ))}
                        </>
                    )}
                </div>

                {/* Progress bar */}
                {!permissionError && (
                    <div className="shrink-0 h-0.5 sm:h-1 bg-white/10 sm:bg-gray-100">
                        <div
                            className="h-full bg-red-500 transition-all duration-1000"
                            style={{ width: isRecording ? `${progress}%` : "0%" }}
                        />
                    </div>
                )}

                {/* Controls */}
                {!permissionError && (
                    <div className="shrink-0 flex items-center justify-between px-4 sm:px-5 py-3 sm:py-4 bg-black/70 sm:bg-white border-t border-white/10 sm:border-gray-200 gap-3">
                        {/* Left: orientation + flip */}
                        <div className="flex items-center gap-2">
                            {!isRecording && (
                                <>
                                    {!portraitOnly && (
                                        <Button
                                            type="button"
                                            size="sm"
                                            variant="outline"
                                            disabled={cameraLoading}
                                            onClick={handleToggleOrientation}
                                            className="text-xs border-white/40 text-white bg-white/10 hover:bg-white/20 hover:text-white sm:border-input sm:text-foreground sm:bg-background sm:hover:bg-accent sm:hover:text-accent-foreground"
                                        >
                                            {orientation === "landscape" ? "Portrait" : "Landscape"}
                                        </Button>
                                    )}
                                    <Button
                                        type="button"
                                        size="icon"
                                        variant="outline"
                                        disabled={cameraLoading}
                                        onClick={handleFlipCamera}
                                        aria-label="Flip camera"
                                        className="border-white/40 text-white bg-white/10 hover:bg-white/20 hover:text-white sm:border-input sm:text-foreground sm:bg-background sm:hover:bg-accent sm:hover:text-accent-foreground"
                                    >
                                        <RefreshCcw className="h-4 w-4" />
                                    </Button>
                                    {captionGuide && (
                                        <Button
                                            type="button"
                                            size="icon"
                                            variant="outline"
                                            disabled={cameraLoading}
                                            aria-label={captionGuide.enabled ? "Hide guides" : "Show guides"}
                                            onClick={captionGuide.onToggle}
                                            className={cn(
                                                "min-[640px]:w-auto min-[640px]:px-3 text-xs border-white/40 text-white bg-white/10 hover:bg-white/20 hover:text-white sm:border-gray-300 sm:text-primary sm:bg-white sm:hover:bg-gray-100 sm:hover:text-primary",
                                                !captionGuide.enabled && "opacity-60"
                                            )}
                                        >
                                            <span className="relative inline-flex">
                                                <ScanFace className="h-4 w-4" />
                                                {/* A phone hides the label, so off has to show on the icon. */}
                                                {!captionGuide.enabled && <Slash className="absolute inset-0 h-4 w-4" />}
                                            </span>
                                            {/* Icon only on a phone's narrow bar. */}
                                            <span className={cn("hidden min-[640px]:inline", !captionGuide.enabled && "line-through")}>Guides</span>
                                        </Button>
                                    )}
                                </>
                            )}
                            {isRecording && (
                                <span className="text-sm text-white/70 sm:text-gray-500">
                                    {formatTime(elapsed)} / {formatTime(MAX_RECORDING_SECONDS)}
                                </span>
                            )}
                        </div>

                        {/* Right: record / stop / cancel */}
                        <div className="flex items-center gap-2">
                            {!isRecording ? (
                                <>
                                    <Button
                                        type="button"
                                        size="sm"
                                        onClick={startRecording}
                                        disabled={cameraLoading}
                                        className="flex items-center gap-1.5"
                                    >
                                        {showButtonSpinner ? (
                                            <><Loader2 className="h-3.5 w-3.5 animate-spin" /> Starting…</>
                                        ) : (
                                            <><Camera className="h-4 w-4" /> Start</>
                                        )}
                                    </Button>
                                    <Button
                                        type="button"
                                        size="sm"
                                        variant="destructive"
                                        onClick={handleClose}
                                    >
                                        Cancel
                                    </Button>
                                </>
                            ) : (
                                <Button
                                    type="button"
                                    size="sm"
                                    variant="destructive"
                                    onClick={stopRecording}
                                    className="flex items-center gap-1.5"
                                >
                                    <Square className="h-3.5 w-3.5 fill-current" />
                                    Stop
                                </Button>
                            )}
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default VideoRecorderModal;
